"use client";

import ContentContainer from "@/components/ContentContainer/ContentContainer";
import TabList from "@/components/TabList";
import {
  TABS_BENTO_METADATA,
  TABS_DEFAULT_ORDER,
} from "@/constants/components/tabsBento";
import { MODAL_CONTENT } from "@/constants/modalContent";
import dynamic from "next/dynamic";
import React, { useMemo, useState } from "react";
import type { TabsBentoContainerProps, TabType } from "../../types";
import BentoHeader from "../BentoHeader/BentoHeader";
import BentoTab from "../BentoTab/BentoTab";

const Modal = dynamic(() => import("@/components/Modal"), { ssr: false });

const getTabId = (tabId: TabType) => `bento-tab-${tabId}`;
const getTabPanelId = (tabId: TabType) => `bento-tabpanel-${tabId}`;

const TabsBentoContainer: React.FC<TabsBentoContainerProps> = ({
  defaultTab = "mobile",
  tabOrder = TABS_DEFAULT_ORDER,
  sectionTitle = TABS_BENTO_METADATA.sectionTitle,
  sectionSubtitle = TABS_BENTO_METADATA.sectionSubtitle,
  containerClassName = "max-w-full md:max-w-5xl lg:max-w-384 !px-4 md:!px-8 lg:!px-16",
  onButtonClick,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>(defaultTab);
  const [mountedTabs, setMountedTabs] = useState<Set<TabType>>(
    () => new Set([defaultTab])
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeModalContent, setActiveModalContent] = useState<string | null>(
    null
  );

  const handleButtonClick = (action: string) => {
    console.log(`Button clicked with action: ${action}`);

    // Check if this action corresponds to modal content
    if (MODAL_CONTENT[action]) {
      setActiveModalContent(action);
      setIsModalOpen(true);
    } else if (onButtonClick) {
      onButtonClick(action);
    } else {
      console.log(`Non-modal action: ${action}`);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActiveModalContent(null);
  };

  const mountTab = (tabId: TabType) => {
    setMountedTabs((currentMountedTabs) => {
      if (currentMountedTabs.has(tabId)) {
        return currentMountedTabs;
      }

      const nextMountedTabs = new Set(currentMountedTabs);
      nextMountedTabs.add(tabId);
      return nextMountedTabs;
    });
  };

  const handleTabChange = (tabId: TabType) => {
    setActiveTab(tabId);
    mountTab(tabId);
  };

  // Sort tabs by tabOrder using index lookup map for O(n) performance
  const tabs = useMemo(() => {
    const orderMap = new Map(tabOrder.map((id, index) => [id, index]));
    return [...TABS_BENTO_METADATA.tabs].sort(
      (a, b) =>
        (orderMap.get(a.id) ?? Infinity) - (orderMap.get(b.id) ?? Infinity)
    );
  }, [tabOrder]);

  const modalData = activeModalContent
    ? MODAL_CONTENT[activeModalContent]
    : null;

  return (
    <>
      <ContentContainer className={containerClassName}>
        <BentoHeader title={sectionTitle} subtitle={sectionSubtitle} />

        <div className="py-8">
          <TabList
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            onTabIntent={mountTab}
            className="md:justify-start lg:justify-start"
            tagularProps={{
              location: "bento",
            }}
          />

          {tabs.map((tab) => {
            const isActive = tab.id === activeTab;
            const shouldMountPanelContent = isActive || mountedTabs.has(tab.id);

            return (
              <div
                key={tab.id}
                className="tab-content"
                id={getTabPanelId(tab.id)}
                role="tabpanel"
                aria-labelledby={getTabId(tab.id)}
                hidden={!isActive}
              >
                {shouldMountPanelContent ? (
                  <BentoTab
                    id={tab.id}
                    cards={tab.cards}
                    gridClass={tab.gridClass}
                    layoutType={tab.layoutType}
                    columns={tab.columns}
                    footer={tab.footer}
                    onButtonClick={handleButtonClick}
                    tabType={tab.id}
                    alwaysCarousel={tab.alwaysCarousel}
                    slidesPerViewByBreakpoint={tab.slidesPerViewByBreakpoint}
                    sliderClassName={tab.sliderClassName}
                    sliderOverflowStyle={tab.sliderOverflowStyle}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      </ContentContainer>

      {modalData ? (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={modalData.title}
          actions={modalData.actions}
          tagularProps={{
            name: `modal${modalData.elementName}`,
          }}
        >
          {modalData.content}
        </Modal>
      ) : null}
    </>
  );
};

export default TabsBentoContainer;
